<template>
  <div class="data-analysis-container">
    <div class="nav-container">
      <div v-for="tab in visibleTabs" :key="tab.name" class="nav-item" :class="{ active: activeTabName === tab.name }"
        @click="handleTabClick(tab.name)">
        {{ $str.getKey(tab.label) }}
      </div>
    </div>
    <component v-if="currentComponent" :is="currentComponent" class="component-container"></component>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import useUserStore from '@/store/modules/user'
import { DATA_ANALYSIS_ROOT_PERMISSION, hasAnyPermission } from '@/config/menuPermissionGroups'
// tabs对应的组件
import PowerTrack from './powerTrack/index.vue'
import LoadTrack from './loadTrack/index.vue'
import Demand from './demand/index.vue'
import VoltageConsistency from './voltageConsistency/index.vue'
import Heat from './heat/index.vue'
import MeterData from './meterData/index.vue'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { permissions } = storeToRefs(userStore)

const tabs = [
  { name: 'powerTrack', label: '数据分析/功率跟踪', perms: ['ops:powerTrack:query'] },
  { name: 'loadTrack', label: '数据分析/负荷跟踪', perms: ['ops:loadTrack:query'] },
  { name: 'demand', label: '数据分析/需量分析', perms: ['ops:demandAnalysis:query'] },
  { name: 'voltageConsistency', label: '数据分析/电压一致性', perms: ['ops:voltageConsistency:query'] },
  { name: 'heat', label: '数据分析/温度热管理', perms: ['ops:temperatureThermal:query'] },
  { name: 'meterData', label: '数据分析/电表分析', perms: ['ops:meterAnalysis:query'] },
]

const COMPONENTS = {
  powerTrack: PowerTrack,
  loadTrack: LoadTrack,
  demand: Demand,
  voltageConsistency: VoltageConsistency,
  heat: Heat,
  meterData: MeterData,
}

const VALID_TABS = Object.keys(COMPONENTS)

function hasPerms(perms) {
  return hasAnyPermission(permissions.value, perms)
}

const hasDataAnalysisRoot = computed(() => hasPerms([DATA_ANALYSIS_ROOT_PERMISSION]))
const visibleTabs = computed(() => {
  if (hasDataAnalysisRoot.value) return tabs
  return tabs.filter(tab => hasPerms(tab.perms))
})

function pickInitial() {
  const fromQuery = route.query?.sub
  if (fromQuery && VALID_TABS.includes(fromQuery) && visibleTabs.value.some(tab => tab.name === fromQuery)) return fromQuery
  return visibleTabs.value[0]?.name || ''
}

const activeTabName = ref(pickInitial())

// 路由 ?sub= 变化时切换内部 tab（供"运维菜单"等外部入口跳转使用）
watch(() => route.query?.sub, (val) => {
  if (val && VALID_TABS.includes(val) && visibleTabs.value.some(tab => tab.name === val) && val !== activeTabName.value) {
    activeTabName.value = val
  }
})

watch(visibleTabs, (list, oldList) => {
  if (!list.length) return
  if (!list.some(tab => tab.name === activeTabName.value)) {
    if (!oldList || !oldList.length) {
      activeTabName.value = list[0].name
      return
    }
    handleTabClick(list[0].name)
  }
}, { flush: 'post' })

/** 计算动态组件当前渲染的组件 */
const currentComponent = computed(() => {
  if (!visibleTabs.value.some(tab => tab.name === activeTabName.value)) return null
  return COMPONENTS[activeTabName.value]
})

function handleTabClick(name) {
  if (!visibleTabs.value.some(tab => tab.name === name)) return
  activeTabName.value = name
  // 同步到 URL，刷新/分享可还原
  if (route.query?.sub !== name) {
    router.replace({ query: { ...route.query, sub: name } })
  }
}
</script>

<style lang="scss" scoped>
.data-analysis-container {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;

  .nav-container {
    padding: 14px;
    box-sizing: border-box;
    background: rgba(255, 255, 255, 1);
    display: flex;
    align-items: center;
    border-radius: 20px 20px 0 0;
    gap: 12px;
  }

  .nav-item {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 88px;
    height: 44px;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    font-style: normal;
    line-height: 100%;
    letter-spacing: 0%;
    color: rgba(46, 46, 46, 0.6);
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.3s ease;
    border-radius: 20px;
    background: transparent;

    &:hover {
      color: rgba(46, 46, 46, 0.8);
      background: rgba(0, 0, 0, 0.02);
    }

    &.active {
      color: rgba(58, 104, 255, 1);
      font-weight: 400;
      background: rgba(226, 233, 255, 1);

      &::after {
        display: none;
      }
    }
  }

  .component-container {
    flex: 1;
    min-height: 0;
  }
}
</style>
